<template>
  <div class="progress-control" :class="{ dragging }">
    <div
      ref="holder"
      class="progress-holder"
      @mousedown="onProgressMouseDown"
      @mousemove="onMouseMove"
    >
      <div class="load-progress" :style="`width: ${loadProgress * 100}%`"></div>
      <div
        ref="mouseDisplay"
        class="mouse-display"
        :style="`left: ${mouseDisplayLeft}px`"
      >
        {{ mouseDisplayTime | time }}
      </div>
      <div class="play-progress" :style="`width: ${playProgress * 100}%`">
        <div
          ref="timeTooltip"
          class="time-tooltip"
          :style="`left: ${timeTooltipLeft}px`"
        >
          {{ currentTime | time }}
        </div>
        <div class="bar-cap" @mousemove.stop>
          <div class="bar-cap-inner"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as throttle from 'lodash/throttle'
import timeFilter from '@/utils/timeFilter'

export default {
  filters: {
    time: timeFilter
  },
  props: {
    loadProgress: {
      type: Number,
      default: 0
    },
    playProgress: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      mouseDisplayLeft: 0,
      mouseDisplayTime: 0,
      currentTime: 0,
      timeTooltipLeft: 0,
      dragging: false
    }
  },
  computed: {
    player() {
      return this.$parent.$refs.player
    }
  },
  watch: {
    playProgress(value) {
      const { timeTooltip, holder } = this.$refs
      const offset = timeTooltip.offsetWidth / 2
      const width = value * holder.offsetWidth
      this.currentTime = this.$parent.currentTime
      this.timeTooltipLeft =
        width < offset
          ? 0
          : width + offset > holder.offsetWidth
          ? holder.offsetWidth - timeTooltip.offsetWidth
          : width - offset
    }
  },
  methods: {
    onMouseMove(e) {
      const { mouseDisplay, holder } = this.$refs
      const offset = mouseDisplay.offsetWidth / 2
      this.mouseDisplayLeft =
        e.offsetX < offset
          ? 0
          : e.offsetX + offset > holder.offsetWidth
          ? holder.offsetWidth - mouseDisplay.offsetWidth
          : e.offsetX - offset
      this.mouseDisplayTime =
        (e.offsetX / holder.offsetWidth) * this.$parent.duration
    },
    onProgressMouseDown(e) {
      if (e.button === 0) {
        const holder = e.currentTarget
        this.$parent.pause()
        const onMouseUp = me => {
          onMouseMove(me)
          this.$parent.play()
          onMouseMove.flush()
          document.body.removeEventListener('mouseup', onMouseUp)
          document.body.removeEventListener('mousemove', onMouseMove)
          this.$nextTick(() => {
            this.dragging = false
          })
        }
        const onMouseMove = throttle(me => {
          this.dragging = true
          const offsetLeft =
            this.player.getBoundingClientRect().left +
            this.$el.ownerDocument.defaultView.pageXOffset
          const offsetX =
            me.clientX > offsetLeft
              ? me.clientX > holder.offsetWidth + offsetLeft
                ? holder.offsetWidth
                : me.clientX - offsetLeft
              : 1
          if (this.$parent.duration) {
            const time = (offsetX / holder.offsetWidth) * this.$parent.duration
            this.$parent.setTime(time)
          }
        }, 10)
        document.body.addEventListener('mouseup', onMouseUp)
        document.body.addEventListener('mousemove', onMouseMove)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.progress-holder,
.load-progress,
.play-progress {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}
.progress-holder {
  background-color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
}
.load-progress {
  background-color: rgba(255, 255, 255, 0.65);
}
.play-progress {
  background-color: #3352fe;
  .bar-cap {
    position: absolute;
    width: 14px;
    height: 14px;
    right: -13px;
    top: 50%;
    margin-top: -13px;
    border-radius: 50%;
    background-color: rgba(51, 82, 254, 0.35);
    padding: 6px;
    cursor: pointer;
    display: none;
  }
  .bar-cap-inner {
    width: 100%;
    height: 100%;
    background-color: #3352fe;
    border-radius: 50%;
  }
}
.mouse-display {
  position: absolute;
  bottom: 0;
  margin-bottom: 4px;
  padding: 4px 8px 6px 8px;
  font-size: 12px;
  background-color: #363636;
  color: $white;
  line-height: normal;
  border-radius: 3px;
  visibility: hidden;
  transition: bottom 0.1s ease-in, opacity 0.1s ease-in;
  opacity: 0;
}
.time-tooltip {
  float: right;
  background-color: hsla(0, 0%, 100%, 0.8);
  color: #000000;
  position: absolute;
  font-size: 12px;
  line-height: normal;
  border-radius: 0.3em;
  padding: 4px 8px 6px 8px;
  bottom: 0;
  margin-bottom: 14px;
  visibility: hidden;
  transition: bottom 0.1s ease-in, opacity 0.1s ease-in;
  opacity: 0;
}
.progress-control {
  transition: all 0.2s ease-in;
  &:hover,
  &.dragging {
    .time-tooltip {
      visibility: visible;
      bottom: 100%;
      opacity: 1;
    }
    .bar-cap {
      display: block;
    }
  }
  &:hover {
    .mouse-display {
      visibility: visible;
      bottom: 100%;
      opacity: 1;
    }
  }
}
</style>
